import { useSyncExternalStore } from 'react';

import {
	SMART_FOLDER_ROVO_CONNECTOR_PROVIDERS,
	type SmartFolderRovoConnectorProvider,
} from './SmartFolderRovoConnectorProviders.ts';

const SMART_FOLDER_PROPERTIES_KEY = 'smartFolderProperties';
const DEFAULT_PROVIDER = SMART_FOLDER_ROVO_CONNECTOR_PROVIDERS['google-drive'];

export type ContentPropertyNode = {
	readonly key?: string | null;
	readonly value?: unknown;
} | null;

type SmartFolderContentProperties = {
	readonly appName?: unknown;
	readonly containerApp?: unknown;
	readonly containerAri?: unknown;
	readonly lastSynced?: unknown;
	readonly paused?: unknown;
	readonly provider?: unknown;
	readonly value?: unknown;
};

export type SmartFolderQuickActionSource = {
	appName: string;
	provider: SmartFolderRovoConnectorProvider;
};

type UseSmartFolderQuickActionSourceOptions = {
	skip?: boolean;
};

const normalizeProviderName = (name: string): string =>
	name
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]/g, '');

const providerByContainerApp = {
	[normalizeProviderName('Google Drive')]: 'google-drive',
	[normalizeProviderName('google-drive')]: 'google-drive',
	[normalizeProviderName('google-drive-connector')]: 'google-drive',
	[normalizeProviderName('smartlinks-connector-gdrive')]: 'google-drive',
	[normalizeProviderName('smartlinks-connector-gdrive-rsl')]: 'google-drive',
	[normalizeProviderName('lite-connector-gdrive')]: 'google-drive',
	[normalizeProviderName('SharePoint')]: 'sharepoint',
	[normalizeProviderName('sharepoint')]: 'sharepoint',
	[normalizeProviderName('sharepoint-connector')]: 'sharepoint',
	[normalizeProviderName('smartlinks-connector-sharepoint')]: 'sharepoint',
	[normalizeProviderName('ai-3p-connector:sharepoint-domain')]: 'sharepoint',
	[normalizeProviderName('OneDrive')]: 'onedrive',
	[normalizeProviderName('onedrive')]: 'onedrive',
	[normalizeProviderName('smartlinks-connector-onedrive')]: 'onedrive',
	[normalizeProviderName('onedrive-connector')]: 'onedrive',
} satisfies Record<string, SmartFolderRovoConnectorProvider>;

const rememberedSources = new Map<string, SmartFolderQuickActionSource>();
const subscribers = new Set<() => void>();

const subscribe = (callback: () => void) => {
	subscribers.add(callback);

	return () => {
		subscribers.delete(callback);
	};
};

const skipSubscribe = () => () => {};
const getSkippedRememberedSource = () => undefined;

const notifySubscribers = () => {
	subscribers.forEach((callback) => callback());
};

const getSourceFromProvider = (
	provider?: SmartFolderRovoConnectorProvider,
): SmartFolderQuickActionSource | undefined => {
	const providerConfig = provider ? SMART_FOLDER_ROVO_CONNECTOR_PROVIDERS[provider] : undefined;

	return provider && providerConfig
		? {
				appName: providerConfig.displayName,
				provider,
			}
		: undefined;
};

const getProviderFromContainerAri = (
	containerAri?: string,
): SmartFolderRovoConnectorProvider | undefined => {
	if (!containerAri) {
		return undefined;
	}

	const normalizedContainerAri = containerAri.toLowerCase();

	if (
		normalizedContainerAri.includes('microsoft.sharepoint') ||
		normalizedContainerAri.includes(':sharepoint::')
	) {
		return 'sharepoint';
	}

	if (
		normalizedContainerAri.includes('microsoft.onedrive') ||
		normalizedContainerAri.includes(':onedrive::')
	) {
		return 'onedrive';
	}

	if (
		normalizedContainerAri.includes('google.google-drive') ||
		normalizedContainerAri.includes(':drive::')
	) {
		return 'google-drive';
	}

	return undefined;
};

const getSourceFromContainerApp = (
	containerApp?: string,
): SmartFolderQuickActionSource | undefined =>
	getSourceFromProvider(
		containerApp ? providerByContainerApp[normalizeProviderName(containerApp)] : undefined,
	);

const parseSmartFolderContentProperties = (
	propertyValue: unknown,
): SmartFolderContentProperties | undefined => {
	let parsedValue = propertyValue;

	try {
		for (let unwrapCount = 0; unwrapCount < 3; unwrapCount += 1) {
			if (typeof parsedValue === 'string') {
				parsedValue = JSON.parse(parsedValue) as SmartFolderContentProperties | string;
				continue;
			}

			if (typeof parsedValue !== 'object' || parsedValue === null) {
				return undefined;
			}

			const contentProperties = parsedValue as SmartFolderContentProperties;
			if (
				typeof contentProperties.value === 'string' ||
				typeof contentProperties.value === 'object'
			) {
				parsedValue = contentProperties.value;
				continue;
			}

			return contentProperties;
		}
	} catch {
		return undefined;
	}

	return typeof parsedValue === 'object' && parsedValue !== null
		? (parsedValue as SmartFolderContentProperties)
		: undefined;
};

const getSourceFromPropertyValue = (
	propertyValue: unknown,
): SmartFolderQuickActionSource | undefined => {
	const parsedValue = parseSmartFolderContentProperties(propertyValue);
	if (!parsedValue) {
		return undefined;
	}

	const containerApp =
		typeof parsedValue.containerApp === 'string'
			? parsedValue.containerApp
			: typeof parsedValue.appName === 'string'
				? parsedValue.appName
				: undefined;
	const sourceFromContainerApp = getSourceFromContainerApp(containerApp);
	if (sourceFromContainerApp) {
		return sourceFromContainerApp;
	}

	const providerName = typeof parsedValue.provider === 'string' ? parsedValue.provider : undefined;
	const sourceFromProvider = getSourceFromContainerApp(providerName);
	if (sourceFromProvider) {
		return sourceFromProvider;
	}

	const containerAri =
		typeof parsedValue.containerAri === 'string' ? parsedValue.containerAri : undefined;
	return getSourceFromProvider(getProviderFromContainerAri(containerAri));
};

const getSourceFromProperties = (
	properties?: readonly ContentPropertyNode[] | null,
): SmartFolderQuickActionSource | undefined => {
	const smartFolderProperties = properties?.find(
		(property) => property?.key === SMART_FOLDER_PROPERTIES_KEY,
	);

	return smartFolderProperties?.value
		? getSourceFromPropertyValue(smartFolderProperties.value)
		: undefined;
};

export const getIsSmartFolderSyncPaused = (
	properties?: readonly ContentPropertyNode[] | null,
): boolean => {
	const smartFolderProperties = properties?.find(
		(property) => property?.key === SMART_FOLDER_PROPERTIES_KEY,
	);

	return parseSmartFolderContentProperties(smartFolderProperties?.value)?.paused === true;
};

export const getSmartFolderLastSynced = (
	properties?: readonly ContentPropertyNode[] | null,
): string | null => {
	const smartFolderProperties = properties?.find(
		(property) => property?.key === SMART_FOLDER_PROPERTIES_KEY,
	);
	const lastSynced = parseSmartFolderContentProperties(smartFolderProperties?.value)?.lastSynced;

	return typeof lastSynced === 'string' ? lastSynced : null;
};

export const rememberSmartFolderQuickActionSource = ({
	containerApp,
	folderId,
}: {
	containerApp?: string;
	folderId: string;
}): void => {
	const source = getSourceFromContainerApp(containerApp);
	if (!source) {
		return;
	}

	rememberedSources.set(folderId, source);
	notifySubscribers();
};

export const getSmartFolderQuickActionSource = (
	properties?: readonly ContentPropertyNode[] | null,
): SmartFolderQuickActionSource =>
	getSourceFromProperties(properties) ?? {
		appName: DEFAULT_PROVIDER.displayName,
		provider: DEFAULT_PROVIDER.provider,
	};

export const useSmartFolderQuickActionSource = (
	folderId: string,
	properties?: readonly ContentPropertyNode[] | null,
	options: UseSmartFolderQuickActionSourceOptions = {},
): SmartFolderQuickActionSource => {
	const { skip = false } = options;
	const rememberedSource = useSyncExternalStore(
		skip ? skipSubscribe : subscribe,
		skip ? getSkippedRememberedSource : () => rememberedSources.get(folderId),
		getSkippedRememberedSource,
	);

	if (skip) {
		return {
			appName: DEFAULT_PROVIDER.displayName,
			provider: DEFAULT_PROVIDER.provider,
		};
	}

	return (
		getSourceFromProperties(properties) ??
		rememberedSource ?? {
			appName: DEFAULT_PROVIDER.displayName,
			provider: DEFAULT_PROVIDER.provider,
		}
	);
};
